function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Checkbox, FullPageModal, FullPageModalAdditionalActions, FullPageModalFooterEnd, FullPageModalFooterStart, FullPageModalHeaderStart, Grid, ConditionalWrapper, ScrollToTopButton, Tooltip, Typography, useDidUpdate, useIsMounted, WithInfoIcon, Separator } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { accountantAccessInfoTooltip } from "../../../business-logic/accountantAccessLogic";
import { isAutoApprovalValid } from "../../../business-logic/autoApprovalLogic";
import { hasYearlyAutomaticBillingError } from "../../../business-logic/billingLogic";
import IntroVideoField from "./IntroVideo/IntroVideoField";
import ErrorBoundaryPage, { makeSentryIgnoredError } from "../../ErrorBoundaryPage";
import { businessSettingsDefaults } from "../../../constants";
import { useStore, useStoreActions } from "../../../contexts";
import { useContacts } from "../../../hooks/reactQuery/queries/useContacts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { useSignal } from "../../../hooks/useSignal";
import { scrollIntoView, wait } from "../../../infra/utils";
import { approvalMethodNoticePeriodModel, legalTermsModel, serviceModel } from "../../../models";
import { AgreementDataModel } from "../../../models/AgreementDataModel";
import { makeExpirationDate } from "../../../models/expiration-date";
import { makeIntroVideoNone } from "../../../models/intro-video/makers";
import { RelationshipStatus } from "../../../models/relationship";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../../models/request-accountant-access";
import { useValidateService } from "../../../models/service/hooks/useValidateService";
import { toDateStampObject } from "../../../models/time";
import { selectors } from "../../../store/state-selectors";
import { useAppConfig } from "../../../store/store-context";
import { AllowManualPaymentsCheckbox, ApprovalMethodNoticePeriodField, CoverCreditCardFeesSelector } from "../../agreement-controllers";
import LegalTermsDropdownOld from "../../LegalTermsDropdownOld/LegalTermsDropdownOld";
import NetTermsDropdown from "../../NetTermsDropdown/NetTermsDropdown";
import styles from "../AgreementStudioPage.module.scss";
import { defaultCoverLetterNewClient } from "../AgreementStudioPage.utils";
import ExpirationDate from "./ExpirationDate/ExpirationDate";
import ExtraClause from "./ExtraClause/ExtraClause";
import FormlingContainer from "./FormlingContainer/FormlingContainer";
import IntroMessage, { validateIntroMessage } from "./IntroMessage/IntroMessage";
import { LegalTerms } from "./LegalTerms/LegalTerms";
import ServicePackages from "./ServicePackages/ServicePackages";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const InnerSep = props => /*#__PURE__*/_jsx("div", {
  className: cn(styles.innerSeparator, {
    [styles.thin]: props.thin
  })
});
export let SubmitType = /*#__PURE__*/function (SubmitType) {
  SubmitType["SendSilently"] = "sendSilently";
  SubmitType["SendProposal"] = "sendProposal";
  SubmitType["ExternallySign"] = "externallySign";
  SubmitType["ScheduleProposal"] = "scheduleProposal";
  return SubmitType;
}({});
export const GetAgreementStudioModalTID = () => getTestIds({
  deprecatedTestIds: {
    AgreementStudioModalTID: {
      SubmitButton: 'agreement-studio-topbar-btn-submit'
    }
  }
});
export default function AgreementStudioModal(props) {
  var _draft$coverLetter, _draft$legalTermsOld, _draft$legalTermsOld2, _draft$legalTermsOld3, _draft$legalTermsOld4, _worksAt$settings, _worksAt$settings2, _worksAt$settings3, _worksAt$settings4, _draft$expirationDate, _worksAt$settings5, _draft$expirationDate2, _worksAt$settings6, _worksAt$settings7, _worksAt$settings8, _worksAt$settings11;
  const {
    isTemplateProposal,
    draft,
    legalTermsDocuments,
    worksAt,
    autoSaveTimeout = 1500,
    isSampleRelationship = false,
    agreementTitleMP,
    savingStatusMP,
    modalTitle,
    modalTitleTestId,
    effectiveDate,
    additionalAutoSaveDependencies = [],
    inlineFooterActions,
    additionalFooterActions,
    doScrollToTopRef,
    contact,
    isReadOnly,
    onSubmitCb,
    onClose: _onClose,
    shouldClose = async () => true,
    onAutoSaveCb,
    openPreviewModal,
    onUploadLegalTermsDocument,
    renderSubmitButton
  } = props;
  const {
    AgreementStudioModalTID
  } = useMemo(() => GetAgreementStudioModalTID(), []);
  const featureFlags = useFeatureFlags();
  const {
    logBI
  } = useStoreActions();
  const isMounted = useIsMounted();
  const {
    error: contactsError
  } = useContacts();
  const {
    disableScrollAnimation
  } = useAppConfig();
  const defaultIntroVideo = useStore(selectors.user.introVideo);
  const {
    MULTIPLE_LEGAL_TERMS_DOCUMENTS
  } = featureFlags;
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const includingExpenses = false;
  const isExistingCustomer = Boolean(draft === null || draft === void 0 ? void 0 : draft.isExistingCustomer);
  const coverLetterValid = true;
  const coverLetter = (_draft$coverLetter = draft === null || draft === void 0 ? void 0 : draft.coverLetter) !== null && _draft$coverLetter !== void 0 ? _draft$coverLetter : defaultCoverLetterNewClient;
  const [services, setServices] = useState(() => draft ? draft.services : []);
  const [bundles, setBundles] = useState(() => draft && draft.serviceBundles ? draft.serviceBundles.bundles.map(b => _objectSpread(_objectSpread({}, b), {}, {
    services: b.services
  })) : []);
  const businessAndDraftLegalTermDocs = useMemo(() => {
    if (legalTermsDocuments.some(_ref => {
      let {
        id
      } = _ref;
      return (draft === null || draft === void 0 ? void 0 : draft.legalTermsOld) != null &&
      // eslint-disable-next-line no-unsafe-optional-chaining
      'libraryLegalTermsId' in (draft === null || draft === void 0 ? void 0 : draft.legalTermsOld) && id === draft.legalTermsOld.libraryLegalTermsId;
    })) {
      return legalTermsDocuments;
    } else if ((draft === null || draft === void 0 ? void 0 : draft.legalTermsOld) != null) {
      return legalTermsDocuments.concat([legalTermsModel.fromDraftLegalTermsOld(draft === null || draft === void 0 ? void 0 : draft.legalTermsOld)]);
    } else {
      return legalTermsDocuments;
    }
  }, [legalTermsDocuments, draft === null || draft === void 0 ? void 0 : draft.legalTermsOld]);
  const {
    draftHasPdf,
    isLegalTermsFromLibrary,
    isUploadPdfMode
  } = useMemo(() => {
    const draftHasPdf = !!(draft !== null && draft !== void 0 && draft.legalTermsOld) && draft.legalTermsOld.type === 'pdf';
    const legalTermsId = (draft === null || draft === void 0 ? void 0 : draft.legalTermsOld) != null && 'libraryLegalTermsId' in draft.legalTermsOld && draft.legalTermsOld.libraryLegalTermsId;
    const isLegalTermsFromLibrary = legalTermsId && businessAndDraftLegalTermDocs.some(doc => doc.id === legalTermsId);
    const isUploadPdfMode = draftHasPdf && !isLegalTermsFromLibrary;
    return {
      draftHasPdf,
      isLegalTermsFromLibrary,
      isUploadPdfMode
    };
  }, [draft, businessAndDraftLegalTermDocs]);
  const [tosPdfFilename, setTosPdfFilename] = useState(draftHasPdf && (draft === null || draft === void 0 || (_draft$legalTermsOld = draft.legalTermsOld) === null || _draft$legalTermsOld === void 0 ? void 0 : _draft$legalTermsOld.type) === 'pdf' ? draft === null || draft === void 0 || (_draft$legalTermsOld2 = draft.legalTermsOld) === null || _draft$legalTermsOld2 === void 0 ? void 0 : _draft$legalTermsOld2.displayName : null);
  const [tosPdfUniqueName, setTosPdfUniqueName] = useState(isUploadPdfMode && (draft === null || draft === void 0 || (_draft$legalTermsOld3 = draft.legalTermsOld) === null || _draft$legalTermsOld3 === void 0 ? void 0 : _draft$legalTermsOld3.type) === 'pdf' ? draft === null || draft === void 0 || (_draft$legalTermsOld4 = draft.legalTermsOld) === null || _draft$legalTermsOld4 === void 0 ? void 0 : _draft$legalTermsOld4.pdfUniqueName : null);
  const [tosFileId, setTosFileId] = useState(() => {
    const {
      legalTermsDocumentId
    } = worksAt.settings || {};
    // eslint-disable-next-line no-unsafe-optional-chaining
    return isLegalTermsFromLibrary && (draft === null || draft === void 0 ? void 0 : draft.legalTermsOld) != null && 'libraryLegalTermsId' in (draft === null || draft === void 0 ? void 0 : draft.legalTermsOld) ? draft.legalTermsOld.libraryLegalTermsId : legalTermsDocumentId || null;
  });
  const [agreementLegalTerms, setAgreementLegalTerms] = useState(() => {
    if (MULTIPLE_LEGAL_TERMS_DOCUMENTS) {
      const {
        legalTermsDocumentId
      } = worksAt.settings || {};
      if (draft !== null && draft !== void 0 && draft.legalTermsDocuments && draft.legalTermsDocuments.length > 0) {
        return draft.legalTermsDocuments;
      }
      const legalTermsDocument = legalTermsDocuments.find(_ref2 => {
        let {
          id
        } = _ref2;
        return id === legalTermsDocumentId;
      });
      if (legalTermsDocument) {
        return [legalTermsModel.toRelationshipLegalTermsDocuments(legalTermsDocument)];
      }
      return [];
    }
    return [];
  });
  const computeLegalTermErrors = useCallback(legalTerms => {
    return legalTerms.map(legalTerm => {
      var _legalTerm$termsOfSer;
      const errors = {};
      if (legalTerm === null) {
        errors.legalTermField = 'Please choose a document';
      }
      if ((legalTerm === null || legalTerm === void 0 || (_legalTerm$termsOfSer = legalTerm.termsOfService) === null || _legalTerm$termsOfSer === void 0 ? void 0 : _legalTerm$termsOfSer.type) === 'explicitApproval') {
        var _legalTerm$termsOfSer2, _legalTerm$termsOfSer3;
        if (!((_legalTerm$termsOfSer2 = legalTerm.termsOfService.explicitApproval.statement) !== null && _legalTerm$termsOfSer2 !== void 0 && _legalTerm$termsOfSer2.trim())) {
          errors.explicitApproval = 'Please enter a checkbox statement';
        } else if (((_legalTerm$termsOfSer3 = legalTerm.termsOfService.explicitApproval.statement) === null || _legalTerm$termsOfSer3 === void 0 ? void 0 : _legalTerm$termsOfSer3.length) > 120) {
          errors.explicitApproval = 'The statement can be up to 120 characters';
        }
      }
      return errors;
    });
  }, []);

  // Check if there are validation errors - does NOT depend on wasSubmitted
  const hasLegalTermsErrors = useCallback(() => {
    if (!MULTIPLE_LEGAL_TERMS_DOCUMENTS) return false;
    return computeLegalTermErrors(agreementLegalTerms).some(error => error.legalTermField || error.explicitApproval);
  }, [MULTIPLE_LEGAL_TERMS_DOCUMENTS, agreementLegalTerms, computeLegalTermErrors]);

  // Errors to display in UI - only show after submit attempt
  const legalTermsErrors = useMemo(() => {
    if (!MULTIPLE_LEGAL_TERMS_DOCUMENTS || !wasSubmitted) {
      return [];
    }
    return computeLegalTermErrors(agreementLegalTerms);
  }, [MULTIPLE_LEGAL_TERMS_DOCUMENTS, wasSubmitted, agreementLegalTerms, computeLegalTermErrors]);
  const [amendmentAutoApproval, setAmendmentAutoApproval] = useState(draft !== null && draft !== void 0 && draft.amendmentsApprovalMethod ? approvalMethodNoticePeriodModel.isAutoApproval(draft.amendmentsApprovalMethod) : !!((_worksAt$settings = worksAt.settings) !== null && _worksAt$settings !== void 0 && _worksAt$settings.amendmentsApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod));
  const [amendmentAutoApprovalNoticePeriod, setAmendmentAutoApprovalNoticePeriod] = useState(draft !== null && draft !== void 0 && draft.amendmentsApprovalMethod && approvalMethodNoticePeriodModel.isAutoApproval(draft.amendmentsApprovalMethod) ? draft.amendmentsApprovalMethod.auto.noticePeriodDays : (_worksAt$settings2 = worksAt.settings) !== null && _worksAt$settings2 !== void 0 && _worksAt$settings2.amendmentsApprovalMethod && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod) ? worksAt.settings.amendmentsApprovalMethod.auto.noticePeriodDays : 30);
  const [invoiceAutoApproval, setInvoiceAutoApproval] = useState(draft !== null && draft !== void 0 && draft.invoicesApprovalMethod ? approvalMethodNoticePeriodModel.isAutoApproval(draft.invoicesApprovalMethod) : !!((_worksAt$settings3 = worksAt.settings) !== null && _worksAt$settings3 !== void 0 && _worksAt$settings3.invoicesApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.invoicesApprovalMethod));
  const [invoiceAutoApprovalNoticePeriod, setInvoiceAutoApprovalNoticePeriod] = useState(draft !== null && draft !== void 0 && draft.invoicesApprovalMethod && approvalMethodNoticePeriodModel.isAutoApproval(draft.invoicesApprovalMethod) ? draft.invoicesApprovalMethod.auto.noticePeriodDays : (_worksAt$settings4 = worksAt.settings) !== null && _worksAt$settings4 !== void 0 && _worksAt$settings4.invoicesApprovalMethod && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.invoicesApprovalMethod) ? worksAt.settings.invoicesApprovalMethod.auto.noticePeriodDays : 30);
  const [introVideo, setIntroVideo] = useState((draft === null || draft === void 0 ? void 0 : draft.introVideo) || defaultIntroVideo || makeIntroVideoNone());
  const [daysToExpiration, setDaysToExpiration] = useState((draft === null || draft === void 0 || (_draft$expirationDate = draft.expirationDate) === null || _draft$expirationDate === void 0 ? void 0 : _draft$expirationDate.type) === 'days' ? draft.expirationDate.days : (worksAt === null || worksAt === void 0 || (_worksAt$settings5 = worksAt.settings) === null || _worksAt$settings5 === void 0 || (_worksAt$settings5 = _worksAt$settings5.expirationDate) === null || _worksAt$settings5 === void 0 ? void 0 : _worksAt$settings5.type) === 'days' ? worksAt.settings.expirationDate.days : 30);
  const [hasExpirationDate, setHasExpirationDate] = useState(draft ? (draft === null || draft === void 0 || (_draft$expirationDate2 = draft.expirationDate) === null || _draft$expirationDate2 === void 0 ? void 0 : _draft$expirationDate2.type) === 'days' : (worksAt === null || worksAt === void 0 || (_worksAt$settings6 = worksAt.settings) === null || _worksAt$settings6 === void 0 || (_worksAt$settings6 = _worksAt$settings6.expirationDate) === null || _worksAt$settings6 === void 0 ? void 0 : _worksAt$settings6.type) === 'days');
  const [netTerms, setNetTerms] = useState((draft === null || draft === void 0 ? void 0 : draft.netTerms) != null ? draft === null || draft === void 0 ? void 0 : draft.netTerms : ((_worksAt$settings7 = worksAt.settings) === null || _worksAt$settings7 === void 0 ? void 0 : _worksAt$settings7.netTerms) || 0);
  const form = useForm({
    mode: 'all',
    defaultValues: {
      extraClause: (draft === null || draft === void 0 ? void 0 : draft.extraClause) || ''
    }
  });
  const [extraClause] = form.watch(['extraClause']);
  const [extraClauseValid, setExtraClauseValid] = useState(true);
  const [requirePaymentMethod, setRequirePaymentMethod] = useState(!isSampleRelationship && (draft === null || draft === void 0 ? void 0 : draft.requirePaymentMethod) !== false);
  const [allowManualPayments, setAllowManualPayments] = useState(!!(draft !== null && draft !== void 0 && draft.allowManualPayments));
  const [transactionFees, setTransactionFees] = useState(!!(draft ? draft.coverCreditCardFees : (_worksAt$settings8 = worksAt.settings) === null || _worksAt$settings8 === void 0 || (_worksAt$settings8 = _worksAt$settings8.feeControls) === null || _worksAt$settings8 === void 0 ? void 0 : _worksAt$settings8.coverCreditCardFees));
  let initialText;
  if (isExistingCustomer) {
    var _worksAt$settings$int, _worksAt$settings9;
    initialText = (_worksAt$settings$int = (_worksAt$settings9 = worksAt.settings) === null || _worksAt$settings9 === void 0 ? void 0 : _worksAt$settings9.introductoryMessages.invitation) !== null && _worksAt$settings$int !== void 0 ? _worksAt$settings$int : businessSettingsDefaults.introductoryMessages.invitation;
  } else {
    var _worksAt$settings$int2, _worksAt$settings10;
    initialText = (_worksAt$settings$int2 = (_worksAt$settings10 = worksAt.settings) === null || _worksAt$settings10 === void 0 ? void 0 : _worksAt$settings10.introductoryMessages.proposal) !== null && _worksAt$settings$int2 !== void 0 ? _worksAt$settings$int2 : businessSettingsDefaults.introductoryMessages.proposal;
  }
  const [customNotificationText, setCustomNotificationText] = useState((draft === null || draft === void 0 ? void 0 : draft.customNotificationText) || initialText);
  const [showNoServicesWarning, setShowNoServicesWarning] = useState(false);
  const [scrollToServiceId, signalScrollToServiceId] = useSignal();
  const [showInvalidPackagesWarning, setShowInvalidPackagesWarning] = useState(false);
  const [showAgreementSettingsWarning, setShowAgreementSettingsWarning] = useState(false);
  const [showExtraClauseWarning, setShowExtraClauseWarning] = useState(false);
  const [showServiceValidationWarning, setShowServiceValidationWarning] = useState([]);
  const [scrollContainer, setScrollContainer] = useState(null);
  const [showAgreementSettings, setShowAgreementSettings] = useState(false);
  const [showPaymentSettings, setShowPaymentSettings] = useState(false);
  const {
    isInvalid
  } = useValidateService();

  // Auto-save stuff
  const hasUnsavedChangesRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const changesSavedTimerRef = useRef(null);
  const [needToSaveDraft, setNeedToSaveDraft] = useState(false);
  const servicePackagesRef = useRef(null);
  const [hasServiceError, setHasServiceError] = useState(false);
  // accountant access
  const [requestAccountantAccess, setRequestAccountantAccess] = useState(draft ? draft.requestAccountantAccess : (_worksAt$settings11 = worksAt.settings) === null || _worksAt$settings11 === void 0 ? void 0 : _worksAt$settings11.requestAccountantAccess);
  const hasRequestAccountantAccess = requestAccountantAccessModel.wasRequested(requestAccountantAccess);
  const agreementData = AgreementDataModel.make({
    netTerms,
    autoPayment: !allowManualPayments,
    status: RelationshipStatus.Pending,
    effectiveDate: effectiveDate && toDateStampObject(effectiveDate),
    daysToEffectiveDate: 0
  });
  useImperativeHandle(doScrollToTopRef, () => {
    return () => scrollContainer === null || scrollContainer === void 0 ? void 0 : scrollContainer.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth'
    });
  });

  // If any relevant fields changed, trigger an autosave
  useDidUpdate(() => {
    hasUnsavedChangesRef.current = true;
    if (isMounted() && onAutoSaveCb != null) {
      if (!autosaveTimerRef.current) {
        const triggerSaveDraft = () => isMounted() && setNeedToSaveDraft(true);
        if (autoSaveTimeout === 0) {
          triggerSaveDraft();
        } else {
          autosaveTimerRef.current = setTimeout(triggerSaveDraft, autoSaveTimeout);
        }
      }
    }
  }, [allowManualPayments, amendmentAutoApproval, amendmentAutoApprovalNoticePeriod, invoiceAutoApproval, invoiceAutoApprovalNoticePeriod, autoSaveTimeout, bundles, coverLetter, customNotificationText, extraClause, includingExpenses, isSampleRelationship, netTerms, introVideo, requirePaymentMethod, services, tosFileId, tosPdfFilename, tosPdfUniqueName, transactionFees, requestAccountantAccess, daysToExpiration, hasExpirationDate, agreementLegalTerms, ...additionalAutoSaveDependencies]);
  useEffect(() => {
    if (needToSaveDraft && onAutoSaveCb != null && isMounted()) {
      setNeedToSaveDraft(false);
      const data = makeAgreementStudioCommon();
      if (data != null) {
        autosaveTimerRef.current = null;
        onAutoSaveCb(data, isMounted);
        hasUnsavedChangesRef.current = false;
      }
    } else {
      hasUnsavedChangesRef.current = false;
    }
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, [needToSaveDraft]);
  useSetDocumentTitle('Agreement Studio');
  useEffect(() => {
    // Cleanup timers.
    return () => {
      autosaveTimerRef.current && clearTimeout(autosaveTimerRef.current);
      // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
      changesSavedTimerRef.current && clearTimeout(changesSavedTimerRef.current);
    };
  }, []);

  // Scroll to show the warnings in agreement settings
  useEffect(() => {
    if (showAgreementSettingsWarning) {
      const errorMessageElement = document.querySelector('[data-testid="agreement-studio-customer-details-input-display-title-helper-text"]') || document.querySelector('[data-testid="pdf-file-not-uploaded-error"]') || document.querySelector('[data-testid="formling-extra-clause-input-helper-text"]');
      if (errorMessageElement) {
        setTimeout(() => scrollIntoView(errorMessageElement, {
          disableScrollAnimation: true
        }), 100);
      }
    }
  }, [showAgreementSettingsWarning]);
  function isServiceInvalid(s) {
    return isInvalid(s, {
      relationshipEffectiveDate: effectiveDate,
      isTemplate: isTemplateProposal
    });
  }
  function makeLegalTermsOld() {
    if (tosFileId != null) {
      const doc = businessAndDraftLegalTermDocs.find(_ref3 => {
        let {
          id
        } = _ref3;
        return id === tosFileId;
      });
      if (doc != null) {
        const isInitialSave = legalTermsDocuments.find(_ref4 => {
          let {
            id
          } = _ref4;
          return id === tosFileId;
        }) == null;
        return legalTermsModel.toDraftLegalTermsOld(doc, isInitialSave);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
  const onOptionalChange = (serviceId, optional, includedByDefault) => applyServiceChange(serviceId, s => serviceModel.setParticipation(s, optional, includedByDefault));
  const onLimitBillingTimesChange = (serviceId, enabled, limit) => applyServiceChange(serviceId, s => serviceModel.setMaxCharges(s, enabled, limit));
  const onQboClassChange = (serviceId, integratedClass) => applyServiceChange(serviceId, s => serviceModel.setIntegratedClass(s, integratedClass));
  const onPausedChange = (serviceId, isPaused) => applyServiceChange(serviceId, s => serviceModel.setPaused(s, isPaused));
  const onBillOnAcceptanceChange = (serviceId, isBillOnAcceptance) => applyServiceChange(serviceId, s => serviceModel.setSkipBillingOnAcceptance(s, !isBillOnAcceptance, featureFlags));
  const onStartBillingAtChange = (serviceId, startBillingAt) => applyServiceChange(serviceId, s => serviceModel.setStartBillingAt(s, startBillingAt));
  const onProratedChange = (serviceId, isProrated) => applyServiceChange(serviceId, s => serviceModel.setProrated(s, isProrated));
  const onAutoPriceIncreaseChange = (serviceId, priceIncrease) => applyServiceChange(serviceId, s => serviceModel.setPriceIncrease(s, priceIncrease));
  function applyServiceChange(serviceId, transformer) {
    setBundles(prevBundles => prevBundles.length ? prevBundles.map(b => _objectSpread(_objectSpread({}, b), {}, {
      services: b.services.map(s => s.id === serviceId ? transformer(s) : s)
    })) : prevBundles);
    setServices(prevServices => prevServices.length ? prevServices.map(s => s.id === serviceId ? transformer(s) : s) : prevServices);
  }
  if (contactsError) {
    throw makeSentryIgnoredError('Failed to fetch contacts list'); // Will trigger the react error-boundary
  }
  const makeAgreementStudioCommon = () => {
    let legalTermsOld = null;
    const legalTermsDocuments = agreementLegalTerms.filter(legalTerm => legalTerm !== null);
    if (!MULTIPLE_LEGAL_TERMS_DOCUMENTS) {
      legalTermsOld = makeLegalTermsOld();
      if (legalTermsOld == null) return null;
    }
    return {
      id: draft === null || draft === void 0 ? void 0 : draft.id,
      services,
      serviceBundles: {
        bundles
      },
      legalTermsOld: legalTermsOld,
      legalTermsDocuments,
      netTerms,
      coverLetter,
      extraClause,
      requirePaymentMethod,
      allowManualPayments,
      coverCreditCardFees: transactionFees,
      isExistingCustomer,
      customNotificationText: customNotificationText,
      amendmentsApprovalMethod: amendmentAutoApproval ? approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(amendmentAutoApprovalNoticePeriod || 30) : approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod(),
      invoicesApprovalMethod: invoiceAutoApproval ? approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(invoiceAutoApprovalNoticePeriod || 30) : approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod(),
      requestAccountantAccess,
      introVideo,
      expirationDate: makeExpirationDate(hasExpirationDate ? daysToExpiration : undefined)
    };
  };
  async function onSubmit(submitType) {
    setWasSubmitted(true);
    while (hasUnsavedChangesRef.current && onAutoSaveCb != null) {
      await wait(300);
    }
    if (!services.length && !bundles.length || bundles.length && bundles.some(b => !b.services.length)) {
      // In case it was true before, this ensures we trigger the useEffect in ServicePackages that switches to the relevant package.
      setShowNoServicesWarning(false);
      setTimeout(() => setShowNoServicesWarning(true));
      scrollRefIntoView(servicePackagesRef);
      return;
    }
    const invalidServices = services.filter(isServiceInvalid).concat(bundles.flatMap(bundle => bundle.services.filter(isServiceInvalid)));
    if (invalidServices.length > 0) {
      signalScrollToServiceId(invalidServices[0].id);
      setShowServiceValidationWarning(invalidServices.map(serviceModel.id));
      return;
    } else {
      setShowServiceValidationWarning([]);
    }

    // Check for yearly automatic billing errors
    const allServices = [...services, ...bundles.flatMap(bundle => bundle.services)];
    const yearlyAutomaticErrorService = allServices.find(service => hasYearlyAutomaticBillingError(service, featureFlags));
    if (yearlyAutomaticErrorService) {
      signalScrollToServiceId(yearlyAutomaticErrorService.id);
      return;
    }
    if (bundles.length === 1) {
      setShowInvalidPackagesWarning(true);
      scrollRefIntoView(servicePackagesRef);
      return;
    }
    if (!coverLetterValid || amendmentAutoApproval && !(amendmentAutoApprovalNoticePeriod && isAutoApprovalValid(amendmentAutoApprovalNoticePeriod))) {
      setShowAgreementSettings(true);
      setShowAgreementSettingsWarning(true);
      return;
    }
    const introMessageError = validateIntroMessage(customNotificationText);
    if (introMessageError) {
      const introMessageElement = document.querySelector('[data-testid="template-content"]');
      if (introMessageElement) {
        scrollIntoView(introMessageElement, {
          disableScrollAnimation
        });
      }
      return;
    }
    if (!extraClauseValid) {
      setShowAgreementSettings(true);
      setShowExtraClauseWarning(true);
      return;
    }
    if (hasServiceError) {
      scrollRefIntoView(servicePackagesRef);
      return;
    }
    if (MULTIPLE_LEGAL_TERMS_DOCUMENTS && hasLegalTermsErrors()) {
      setShowAgreementSettings(true);
      const errorMessageElement = document.querySelector('[aria-invalid="true"]');
      if (errorMessageElement) {
        scrollIntoView(errorMessageElement, {
          disableScrollAnimation
        });
      }
      return;
    }
    const data = makeAgreementStudioCommon();
    if (data != null) {
      autosaveTimerRef.current && clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
      onSubmitCb(data, submitType);
    }
  }
  function scrollRefIntoView(ref) {
    setTimeout(() => {
      ref.current && scrollIntoView(ref.current, {
        disableScrollAnimation
      });
    }, 100);
  }
  return /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
    children: /*#__PURE__*/_jsx(FullPageModal, {
      showFooter: true,
      "data-testid": "full-page-agreement-studio",
      onClose: () => {
        logBI(10480);
        shouldClose(hasUnsavedChangesRef.current).then(confirmed => confirmed && _onClose());
      },
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.mainContainer,
        "data-testid": "agreement-studio",
        children: [/*#__PURE__*/_jsx(FullPageModalHeaderStart, {
          children: /*#__PURE__*/_jsxs("div", {
            className: styles.modalHeader,
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "h5",
              "data-testid": modalTitleTestId,
              children: modalTitle
            }), /*#__PURE__*/_jsx("div", {
              className: styles.statusContainer,
              children: savingStatusMP
            })]
          })
        }), /*#__PURE__*/_jsx("div", {
          className: styles.agreementStudio,
          ref: setScrollContainer,
          "data-testid": "agreement-studio-body",
          children: /*#__PURE__*/_jsxs(ErrorBoundaryPage, {
            bounded: true,
            children: [/*#__PURE__*/_jsx(ScrollToTopButton, {
              "data-testid": "scroll-up-arrow",
              scrollContainer: scrollContainer
            }), /*#__PURE__*/_jsxs("div", {
              style: {
                width: '752px'
              },
              children: [/*#__PURE__*/_jsx(FormlingContainer, {
                title: "Agreement details",
                children: /*#__PURE__*/_jsxs(Grid, {
                  align: "center",
                  rowGap: "small",
                  children: [agreementTitleMP, /*#__PURE__*/_jsx(IntroMessage, {
                    disabled: isReadOnly,
                    onMessageChange: newIntroMsg => setCustomNotificationText(newIntroMsg),
                    initialTemplateContent: customNotificationText
                  }), /*#__PURE__*/_jsx(IntroVideoField, {
                    disabled: isReadOnly,
                    onChange: setIntroVideo,
                    introVideo: introVideo
                  }), /*#__PURE__*/_jsx(ExpirationDate, {
                    disabled: isReadOnly,
                    daysToExpiration: daysToExpiration,
                    setDaysToExpiration: setDaysToExpiration,
                    hasExpirationDate: hasExpirationDate,
                    setHasExpirationDate: setHasExpirationDate
                  })]
                })
              }), /*#__PURE__*/_jsx(FormlingContainer, {
                title: "Services provided",
                children: /*#__PURE__*/_jsx(ServicePackages, {
                  showErrors: wasSubmitted,
                  showEmptyWarning: showNoServicesWarning,
                  scrollToServiceId: scrollToServiceId,
                  ref: servicePackagesRef,
                  showInvalidPackagesWarning: showInvalidPackagesWarning,
                  services: services,
                  bundles: bundles,
                  isReadOnly: isReadOnly,
                  agreementData: agreementData,
                  showServiceValidationWarning: showServiceValidationWarning,
                  advancedSettingsProps: {
                    relationshipState: {
                      type: isTemplateProposal ? 'template' : 'awaitingApproval',
                      effectiveDate: effectiveDate
                    },
                    onAutoPriceIncreaseChange,
                    onPausedChange,
                    onStartBillingAtChange,
                    onBillOnAcceptanceChange,
                    onProratedChange,
                    onOptionalChange,
                    onLimitBillingTimesChange,
                    onQboClassChange,
                    onFinancialCentsChange: (serviceId, financialCents, assignTeamMembers) => applyServiceChange(serviceId, s => serviceModel.setFinancialCentsIntegration(s, financialCents, assignTeamMembers)),
                    onKarbonChange: (serviceId, karbon, assignTeamMembers) => applyServiceChange(serviceId, s => serviceModel.setKarbonIntegration(s, karbon, assignTeamMembers)),
                    onLimitedTimeDiscountOfferChange: (serviceId, limitedTimeDiscountOffer) => applyServiceChange(serviceId, s => serviceModel.setLimitedTimeDiscountOffer(s, limitedTimeDiscountOffer)),
                    onDiscountPeriodExpirationChange: (serviceId, discountPeriodExpiration) => applyServiceChange(serviceId, s => serviceModel.setDiscountPeriodExpiration(s, discountPeriodExpiration)),
                    onPreapprovedAmountChange: (serviceId, amount, controlledByAdvancedSettings) => applyServiceChange(serviceId, s => serviceModel.setPreapprovedAmount(s, amount, controlledByAdvancedSettings)),
                    onServiceChange: updatedService => {
                      applyServiceChange(updatedService.id, () => updatedService);
                    }
                  },
                  isServiceInvalid: isServiceInvalid,
                  setBundles: setBundles,
                  setServices: setServices,
                  onHasError: setHasServiceError
                })
              }), /*#__PURE__*/_jsxs(FormlingContainer, {
                title: "Payment settings",
                togglePanelView: true,
                "data-testid": "agreement-studio-payment-settings-panel",
                isOpen: showPaymentSettings,
                setIsOpen: setShowPaymentSettings,
                children: [/*#__PURE__*/_jsx("div", {
                  className: styles.marginBottom,
                  children: /*#__PURE__*/_jsx(NetTermsDropdown, {
                    netTerms: netTerms,
                    onChange: value => {
                      logBI(10470, {
                        selectedNetTerms: value
                      });
                      setNetTerms(value);
                    },
                    isReadOnly: isReadOnly,
                    testid: "agreement-studio-net-terms-dropdown"
                  })
                }), /*#__PURE__*/_jsx(InnerSep, {
                  thin: true
                }), /*#__PURE__*/_jsx(CoverCreditCardFeesSelector, {
                  vendorBusinessName: worksAt.name,
                  clientBusinessName: contact === null || contact === void 0 ? void 0 : contact.companyName(),
                  value: transactionFees,
                  testid: "agreement-studio-transaction-fees",
                  disabled: isReadOnly,
                  onOpen: () => logBI(10482, {
                    selected: transactionFees
                  }),
                  onChange: selected => {
                    logBI(10483, {
                      selected
                    });
                    setTransactionFees(selected);
                  }
                }), /*#__PURE__*/_jsx(InnerSep, {
                  thin: true
                }), /*#__PURE__*/_jsx(WithInfoIcon, {
                  info: /*#__PURE__*/_jsx(Typography, {
                    skin: "invertedStandard",
                    children: "Select if your client doesn't have a bank account yet, or you don't want us to facilitate payments for now."
                  }),
                  children: /*#__PURE__*/_jsx(ConditionalWrapper, {
                    condition: isSampleRelationship,
                    wrapper: children => /*#__PURE__*/_jsx(Tooltip, {
                      placement: "top",
                      title: /*#__PURE__*/_jsxs(_Fragment, {
                        children: [/*#__PURE__*/_jsx(Typography, {
                          skin: "invertedStandard",
                          weight: "bold",
                          children: "Testing Mode Disables Payments"
                        }), /*#__PURE__*/_jsx(Typography, {
                          skin: "invertedStandard",
                          children: "When creating a proposal with a sample client, this option is disabled. No payment will be processed or saved when you send it, so you can interact with it freely."
                        })]
                      }),
                      children: /*#__PURE__*/_jsx("div", {
                        children: children
                      })
                    }),
                    children: /*#__PURE__*/_jsx(Checkbox, {
                      "data-testid": "agreement-studio-require-payment-method-Checkbox",
                      checked: !requirePaymentMethod && !isSampleRelationship,
                      disabled: isSampleRelationship || isReadOnly,
                      onChange: () => {
                        logBI(requirePaymentMethod ? 10476 : 10475, {});
                        setRequirePaymentMethod(!requirePaymentMethod);
                      },
                      children: /*#__PURE__*/_jsx(Typography, {
                        skin: isSampleRelationship ? 'disabled' : 'standard',
                        children: "Let my client approve the agreement without providing a payment method"
                      })
                    })
                  })
                }), /*#__PURE__*/_jsx(InnerSep, {
                  thin: true
                }), /*#__PURE__*/_jsx(AllowManualPaymentsCheckbox, {
                  isSampleRelationship: isSampleRelationship,
                  isReadOnly: isReadOnly,
                  value: allowManualPayments,
                  onChange: setAllowManualPayments
                })]
              }), /*#__PURE__*/_jsxs(FormlingContainer, {
                title: MULTIPLE_LEGAL_TERMS_DOCUMENTS ? 'Agreement terms' : 'Agreement settings',
                togglePanelView: true,
                isOpen: showAgreementSettings,
                setIsOpen: setShowAgreementSettings,
                "data-testid": "agreement-studio-settings-panel",
                children: [MULTIPLE_LEGAL_TERMS_DOCUMENTS ? /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(LegalTerms, {
                    withExplicitApproval: true,
                    agreementLegalTerms: agreementLegalTerms,
                    onAgreementLegalTermsChange: setAgreementLegalTerms,
                    testid: AgreementStudioModalTID.LegalTerms(),
                    isReadOnly: isReadOnly,
                    errors: legalTermsErrors
                  }), /*#__PURE__*/_jsx(Separator, {})]
                }) : /*#__PURE__*/_jsx(LegalTermsDropdownOld, {
                  onChange: legalTerms => {
                    setTosFileId(legalTerms.id);
                    setTosPdfFilename(legalTerms.name);
                    setTosPdfUniqueName(legalTerms.pdfUniqueName);
                  },
                  label: "Terms & Conditions",
                  selectedLegalTerms: businessAndDraftLegalTermDocs.find(_ref5 => {
                    let {
                      id
                    } = _ref5;
                    return id === tosFileId;
                  }),
                  legalTerms: businessAndDraftLegalTermDocs,
                  showViewTosButton: true,
                  isReadOnly: isReadOnly,
                  onUpload: onUploadLegalTermsDocument,
                  onChangeOld: id => {
                    var _businessAndDraftLega, _businessAndDraftLega2;
                    setTosFileId(id);
                    setTosPdfFilename((_businessAndDraftLega = businessAndDraftLegalTermDocs.find(_ref6 => {
                      let {
                        id
                      } = _ref6;
                      return id === tosFileId;
                    })) === null || _businessAndDraftLega === void 0 ? void 0 : _businessAndDraftLega.name);
                    setTosPdfUniqueName((_businessAndDraftLega2 = businessAndDraftLegalTermDocs.filter(legalTermsModel.isPDF).find(doc => doc.id === tosFileId)) === null || _businessAndDraftLega2 === void 0 ? void 0 : _businessAndDraftLega2.pdfUniqueName);
                  }
                }), /*#__PURE__*/_jsxs("div", {
                  className: styles.checkboxWrapper,
                  children: [/*#__PURE__*/_jsx(ApprovalMethodNoticePeriodField, {
                    info: "Amendments will take effect automatically if the client doesn't disable within the notice period set. Amended invoices issued during the notice period will require client approval.",
                    text: "Automatically approve amendments after a defined period",
                    testid: "amendments-auto-approval",
                    enabled: amendmentAutoApproval,
                    onToggle: setAmendmentAutoApproval,
                    isReadOnly: isReadOnly,
                    noticePeriod: amendmentAutoApprovalNoticePeriod,
                    onChangePeriod: setAmendmentAutoApprovalNoticePeriod
                  }), /*#__PURE__*/_jsx(ApprovalMethodNoticePeriodField, {
                    info: "Invoices that require client approval will be automatically approved within the notice period set, unless approved earlier or if auto-approval is disabled.",
                    text: "Automatically approve out-of-scope charges after a defined period",
                    testid: "invoices-auto-approval",
                    enabled: invoiceAutoApproval,
                    onToggle: setInvoiceAutoApproval,
                    isReadOnly: isReadOnly,
                    noticePeriod: invoiceAutoApprovalNoticePeriod,
                    onChangePeriod: setInvoiceAutoApprovalNoticePeriod
                  }), /*#__PURE__*/_jsx(WithInfoIcon, {
                    info: accountantAccessInfoTooltip,
                    children: /*#__PURE__*/_jsx(Checkbox, {
                      "data-testid": "agreement-studio-accountant-access-checkbox",
                      checked: hasRequestAccountantAccess,
                      disabled: isReadOnly,
                      onChange: () => {
                        const newVal = hasRequestAccountantAccess ? RequestAccountantAccess.None : RequestAccountantAccess.Mandatory;
                        setRequestAccountantAccess(newVal);
                      },
                      children: /*#__PURE__*/_jsx(Typography, {
                        children: "Request Accountant Access to your client's Anchor account"
                      })
                    })
                  })]
                }), /*#__PURE__*/_jsx(ExtraClause, {
                  isReadOnly: isReadOnly,
                  onValidation: setExtraClauseValid,
                  showAgreementSettingsWarning: showExtraClauseWarning
                })]
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(FullPageModalFooterStart, {
          children: /*#__PURE__*/_jsx("div", {
            className: styles.modalFooterStartPhantom
          })
        }), /*#__PURE__*/_jsx(FullPageModalAdditionalActions, {
          inlineActions: compact(inlineFooterActions),
          dropdownActions: compact(additionalFooterActions)
        }), /*#__PURE__*/_jsxs(FullPageModalFooterEnd, {
          children: [/*#__PURE__*/_jsx(Button, {
            skin: "primaryOutline",
            "data-testid": "agreement-studio-topbar-btn-preview",
            onClick: () => {
              const data = makeAgreementStudioCommon();
              if (data != null) openPreviewModal(data);
            },
            children: "View client preview"
          }), renderSubmitButton({
            testid: AgreementStudioModalTID.SubmitButton(),
            className: styles.submitBtn,
            onSubmit
          })]
        })]
      })
    })
  }));
}